import styled from "styled-components";
import { MOBILE_BREAKPOINT } from "../../globalConstants";
import { $LoginBase } from "../LogIn/styles";
import $DropoffMethods from "../DropoffMethods/styles";

const $StartReturnNoIntegration = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 40px 16px;
  box-sizing: border-box;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-height: 100%;
  }

  ${$LoginBase}

  .login-container {
    gap: 24px;
    margin-top: 0;
    margin-bottom: 24px;

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      width: 100%;
      max-width: 456px;
    }
  }

  .login-container .header {
    width: 100%;
  }

  .login-container > form label {
    margin-bottom: 0;
  }
  .login-container > form input,
  .login-container > form select {
    margin-top: 8px;
  }
  .login-container > form input:not([type="radio"]) {
    box-sizing: border-box;
    padding-top: 10px;
    padding-bottom: 10px;
  }
  .login-container > form .dropoff-card input {
    margin-top: 0;
  }

  /* On the dropoff picker step the back-arrow header (rendered inside
     the form) takes the place of the retailer logo + title */
  &.is-step-dropoff .login-container > .logo,
  &.is-step-dropoff .login-container > .header {
    display: none;
  }
`;

export const $Section = styled.section`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  width: 100%;
`;

export const $SectionLabel = styled.h2`
  margin: 0;
  width: 100%;
  font-family: "sofia-pro", sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: var(--soft-black);
`;

// $FieldRow — side-by-side fields (City + State on the Figma mock).
// Collapses to stacked on mobile.
export const $FieldRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;

  > * {
    flex: 1 0 0;
    min-width: 0;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
  }
`;

// $SelectField — wraps the native <select> with the same label-on-top
// layout that <TextInput> uses. There's no shared select component yet;
// when one ships in the design system this whole block can collapse.
export const $SelectField = styled.div`
  label {
    display: block;
    font-weight: 500;
    font-size: 14px;
    color: var(--text-black);
    margin-bottom: 0;
  }
  select {
    -webkit-appearance: none;
    padding: 0 40px 0 16px;
    height: 44px;
    width: 100%;
    border: 2px solid var(--border-card);
    border-radius: 8px;
    font-family: "sofia-pro", sans-serif;
    font-weight: 400;
    font-size: 16px;
    background-color: var(--primary-white);
    color: var(--text-black);

    background-image: linear-gradient(45deg, transparent 50%, var(--soft-black) 50%),
      linear-gradient(135deg, var(--soft-black) 50%, transparent 50%);
    background-position: calc(100% - 20px) 50%, calc(100% - 14px) 50%;
    background-size: 6px 6px, 6px 6px;
    background-repeat: no-repeat;

    &:focus {
      outline: none;
      border: 2px solid var(--primary-white);
      box-shadow: 0 0 0 2px var(--text-black);
    }
  }
`;

// $InfoAlert — gray informational callout under the order number field.
// Matches the Figma "Alert / Default" component. Used to surface the
// "find your order number" helper copy.
export const $InfoAlert = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 16px;
  background-color: var(--disabled-grey);
  border-radius: 8px;
  font-family: "sofia-pro", sans-serif;
  font-size: 14px;
  line-height: 24px;
  color: var(--soft-black);
`;

export const $InfoAlertIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;

  svg {
    width: 24px;
    height: 24px;
  }
  svg path {
    fill: var(--text-black);
  }
`;

// $Divider — full-width 1px line between sections.
export const $Divider = styled.hr`
  width: 100%;
  margin: 0;
  border: 0;
  border-top: 1px solid var(--border-grey);
`;

export const $FooterText = styled.div`
  width: 100%;
  text-align: center;
  font-family: "sofia-pro", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: var(--text-black);

  a {
    color: var(--text-black);
    text-decoration: underline;
  }
`;

// $ButtonRow — full-card-width wrapper for the submit button. The button
// itself is the shared <PrimaryButton width="100%"/>; this just controls
// vertical spacing.
export const $ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`;

export const $Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

export const $DropoffMethodsWrapper = styled($DropoffMethods)`
  .dropoff-card:last-child {
    margin-bottom: 0;
  }
`;

export const $StepHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

export const $StepHeaderTitle = styled.h2`
  flex: 1;
  margin: 0;
  font-family: "sofia-pro", sans-serif;
  font-size: 18px;
  font-weight: 800;
  line-height: 24px;
  text-align: center;
  color: var(--text-black);
`;

// Invisible spacer the same width as SVGButton (icon 24px + padding 10*2
// = 44px) so the title remains centered when the right slot is empty.
export const $StepHeaderSpacer = styled.span`
  width: 28px;
  height: 44px;
  flex-shrink: 0;
`;

export default $StartReturnNoIntegration;
